/* import __COLOCATED_TEMPLATE__ from './phone-runbook.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type AppService from 'embercom/services/app-service';
import type { FinVoiceRunbook } from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import { getLabelForRunbook } from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import { get } from 'embercom/lib/ajax';

interface Procedure {
  id: string;
  name: string;
}

interface GroupedOption {
  heading?: string;
  items: Array<{
    value: string;
    text: string;
  }>;
}

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: $TSFixMe;
    phoneNumberId?: string; // Optional - kept for compatibility but not used
    numberConfiguration?: FinVoiceNumberConfiguration;
    descriptionText?: string;
    updateRunbook: (runbook: FinVoiceRunbook, procedureId: string) => Promise<void>;
  };
}

export default class PhoneRunbook extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare notificationsService: $TSFixMe;

  @tracked selectedRunbook: FinVoiceRunbook = 'none';
  @tracked procedures: Procedure[] = [];
  @tracked selectedProcedure: Procedure | null = null;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);

    this.selectedRunbook = this.savedRunbook || undefined;
    this.loadProcedures();
  }

  async loadProcedures() {
    if (!this.appService.app.canUseFeature('fin-voice-procedures')) {
      return;
    }
    try {
      let response = await get('/ember/fin_voice/procedures', {
        app_id: this.appService.app.id,
      });
      this.procedures = response as Procedure[];
      // Initialize selected procedure after procedures are loaded
      this.resetProcedure();
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t(
          'operator.fin.setup.tabs.setup-and-go-live.phone.runbook.error-loading-procedures',
        ),
      );
    }
  }

  get savingDisabled() {
    return Boolean(
      this.numberConfiguration?.isSaving ||
        (this.selectedRunbook === this.savedRunbook &&
          this.selectedProcedure?.id === this.savedProcedureId),
    );
  }

  get errorMessage() {
    if (
      this.selectedRunbook === this.savedRunbook &&
      this.selectedProcedure?.id === this.savedProcedureId
    ) {
      return this.intl.t(
        'operator.fin.setup.tabs.setup-and-go-live.phone.runbook.error-no-changes',
      );
    }
    return undefined;
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get savedRunbook() {
    return this.numberConfiguration?.runbook || 'none';
  }

  get savedProcedureId() {
    return this.numberConfiguration?.handoverConfiguration.procedureId || '';
  }

  get selectedProcedureId(): string {
    return this.selectedProcedure?.id || '';
  }

  get savedRunbookName() {
    return this.intl.t(
      getLabelForRunbook(this.savedRunbook) ||
        'operator.fin.setup.tabs.setup-and-go-live.phone.runbook.not-set-up',
    );
  }

  @action
  resetRunbook() {
    this.selectedRunbook = this.savedRunbook || undefined;
    this.resetProcedure();
  }

  @action
  resetProcedure() {
    let savedProcedureId = this.numberConfiguration?.handoverConfiguration.procedureId;
    if (savedProcedureId) {
      let procedure = this.procedures.find((p) => p.id === savedProcedureId);
      this.selectedProcedure = procedure || null;
    } else {
      this.selectedProcedure = null;
    }
  }

  @action
  onRunbookChange(runbook: FinVoiceRunbook) {
    this.selectedRunbook = runbook;
  }

  @action
  async updateRunbook() {
    await this.args.updateRunbook(this.selectedRunbook, this.selectedProcedure?.id || '');
  }

  get procedureGroupedOptions(): GroupedOption[] {
    return [
      {
        items: this.procedures.map((procedure) => ({
          value: procedure.id,
          text: procedure.name,
        })),
      },
    ];
  }

  @action
  onProcedureChange(procedureId: string) {
    let procedure = this.procedures.find((p) => p.id === procedureId);
    if (procedure) {
      this.selectedProcedure = procedure;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::NumberConfiguration::PhoneRunbook': typeof PhoneRunbook;
  }
}
